import React, { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { LoadingScreenLayout } from "../layout/LoadingScreenLayout";
import configs from "../../utils/configs";
import styles from "./RoomEntryModal.scss";
import configManager from "../../singleton/ConfigManager";
import { Button } from "@fluentui/react-components";
import {
  ArrowSyncCircle48Regular,
  CalendarLock48Regular,
  ErrorCircle48Regular,
  Info48Regular,
  PersonWarning48Regular,
  PeopleErrorRegular,
  PersonPasskey48Regular,
  PhoneDesktop48Regular,
  PlugDisconnected48Regular,
  Prohibited48Regular,
  QuestionCircle48Regular,
  ShieldLock48Regular,
  ThumbDislike24Regular,
  ThumbLike24Regular,
  Timer48Regular
} from "@fluentui/react-icons";
import classNames from "classnames";
import { AuthPopupRetryDialog } from "../auth/AuthPopupRetryDialog";
import { loginWithDashboardBridgePopup } from "../../utils/dashboard-bridge";
import { closeAuthPopupFromError, getAuthPopupRetryReason } from "../../utils/auth-popup-policy";
import { completeMetaverseLoginAfterBridge } from "../../utils/metaverse-auth-bootstrap";
import { eventRoomContext } from "../../utils/events-utils";

export const ExitReason = {
  exited: "exited",
  closed: "closed",
  denied: "denied",
  disconnected: "disconnected",
  kicked: "kicked",
  left: "left",
  idle: "idle",
  backgrounded: "backgrounded",
  concurrentSession: "concurrentSession",
  deviceLinkedSession: "deviceLinkedSession",
  full: "full",
  sceneError: "sceneError",
  connectError: "connectError",
  versionMismatch: "versionMismatch",
  signInRequired: "signInRequired",
  rsvpRequired: "rsvpRequired",
  notFound: "notFound"
};

const messages = defineMessages({
  [ExitReason.exited]: {
    id: "exited-room-screen.reason.exited",
    defaultMessage: "Your session has ended. Rejoin to start a new one."
  },
  exitedTitle: {
    id: "exited-room-screen.reason.exited-title",
    defaultMessage: "Session Ended"
  },
  [ExitReason.closed]: {
    id: "exited-room-screen.reason.closed",
    defaultMessage: "This Verse is no longer available."
  },
  closedTitle: {
    id: "exited-room-screen.reason.closed-title",
    defaultMessage: "Verse Unavailable"
  },
  [ExitReason.denied]: {
    id: "exited-room-screen.reason.denied",
    defaultMessage: "You are not permitted to join this Verse. Please request permission from the Verse creator."
  },
  deniedTitle: {
    id: "exited-room-screen.reason.denied-title",
    defaultMessage: "Access Denied"
  },
  [ExitReason.disconnected]: {
    id: "exited-room-screen.reason.disconnected",
    defaultMessage: "You were disconnected from the Verse. Check your network and try joining again."
  },
  disconnectedTitle: {
    id: "exited-room-screen.reason.disconnected-title",
    defaultMessage: "Connection Lost"
  },
  [ExitReason.kicked]: {
    id: "exited-room-screen.reason.kicked",
    defaultMessage: "You were removed from this Verse by a moderator."
  },
  kickedTitle: {
    id: "exited-room-screen.reason.kicked-title",
    defaultMessage: "Removed from Verse"
  },
  [ExitReason.left]: {
    id: "exited-room-screen.reason.left",
    defaultMessage: "You have left the Verse"
  },
  [ExitReason.idle]: {
    id: "exited-room-screen.reason.idle",
    defaultMessage: "This session ended because you were inactive for too long. Rejoin to start a new one."
  },
  idleTitle: {
    id: "exited-room-screen.reason.idle-title",
    defaultMessage: "Session Timed Out"
  },
  [ExitReason.backgrounded]: {
    id: "exited-room-screen.reason.backgrounded",
    defaultMessage:
      "This session ended because this page was in the background for too long. Rejoin to start a new one."
  },
  backgroundedTitle: {
    id: "exited-room-screen.reason.backgrounded-title",
    defaultMessage: "Session Timed Out"
  },
  [ExitReason.concurrentSession]: {
    id: "exited-room-screen.reason.concurrent-session",
    defaultMessage: "This session ended because this Verse is open in another tab or device."
  },
  concurrentSessionTitle: {
    id: "exited-room-screen.reason.concurrent-session-title",
    defaultMessage: "Session Switched to Another Device"
  },
  [ExitReason.deviceLinkedSession]: {
    id: "exited-room-screen.reason.device-linked-session",
    defaultMessage:
      "This session ended because you opened this Verse on another device. Rejoin to start a new session here."
  },
  deviceLinkedSessionTitle: {
    id: "exited-room-screen.reason.device-linked-session-title",
    defaultMessage: "Session Moved to Another Device"
  },
  [ExitReason.full]: {
    id: "exited-room-screen.reason.full",
    defaultMessage: "This Verse is full. Please try again later."
  },
  fullTitle: {
    id: "exited-room-screen.reason.full-title",
    defaultMessage: "Verse Is Full"
  },
  [ExitReason.sceneError]: {
    id: "exited-room-screen.reason.scene-error",
    defaultMessage: "Something went wrong while loading this Verse. Try again in a moment."
  },
  sceneErrorTitle: {
    id: "exited-room-screen.reason.scene-error-title",
    defaultMessage: "Verse Failed to Load"
  },
  [ExitReason.connectError]: {
    id: "exited-room-screen.reason.connect-error",
    defaultMessage: "We couldn’t connect to this Verse. Check your connection and try again."
  },
  connectErrorTitle: {
    id: "exited-room-screen.reason.connect-error-title",
    defaultMessage: "Unable to Connect"
  },
  [ExitReason.versionMismatch]: {
    id: "exited-room-screen.reason.version-mismatch",
    defaultMessage: "This Verse is being updated. We’ll refresh automatically in 5 seconds."
  },
  versionMismatchTitle: {
    id: "exited-room-screen.reason.version-mismatch-title",
    defaultMessage: "Verse Updating"
  },
  [ExitReason.signInRequired]: {
    id: "exited-room-screen.reason.sign-in-required",
    defaultMessage: "You must sign in to join this Verse."
  },
  signInRequiredTitle: {
    id: "exited-room-screen.reason.sign-in-required-title",
    defaultMessage: "Sign In Required"
  },
  [ExitReason.rsvpRequired]: {
    id: "exited-room-screen.reason.rsvp-required",
    defaultMessage: "You must RSVP to join this activation."
  },
  rsvpRequiredTitle: {
    id: "exited-room-screen.reason.rsvp-required-title",
    defaultMessage: "RSVP Required"
  },
  [ExitReason.notFound]: {
    id: "exited-room-screen.reason.not-found",
    defaultMessage: "The link may be incorrect, or the Verse may have been removed."
  },
  notFoundTitle: {
    id: "exited-room-screen.reason.not-found-title",
    defaultMessage: "Verse Not Found"
  }
});

export function ExitedRoomScreen({ reason, showTerms, termsUrl, leaveRoomUrl }) {
  const intl = useIntl();

  const [popupRetryReason, setPopupRetryReason] = useState(null);
  const [isPopupLoginPending, setIsPopupLoginPending] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState("idle");
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const retryActionRef = useRef(null);
  const eventContext = eventRoomContext(APP.hub?.user_data);
  const rsvpUrl = eventContext.eventSlug
    ? `${window.location.origin}/xrhub/activations/${encodeURIComponent(
        eventContext.eventSlug
      )}?returnTo=${encodeURIComponent(window.location.href)}`
    : "";
  const eventData = APP.eventData;
  const hasLeaveRoomDestination = Boolean(leaveRoomUrl);
  const shouldShowReturnHomeButton = hasLeaveRoomDestination;
  const shouldShowExitFeedback = reason === ExitReason.left;
  const exitScreenTitle = intl.formatMessage(messages[reason]);
  const exitReasonDetails = {
    [ExitReason.closed]: {
      icon: <Prohibited48Regular />,
      title: intl.formatMessage(messages.closedTitle)
    },
    [ExitReason.denied]: {
      icon: <ShieldLock48Regular />,
      title: intl.formatMessage(messages.deniedTitle),
      body: exitScreenTitle
    },
    [ExitReason.sceneError]: {
      icon: <ErrorCircle48Regular />,
      title: intl.formatMessage(messages.sceneErrorTitle),
      body: exitScreenTitle
    },
    [ExitReason.connectError]: {
      icon: <PlugDisconnected48Regular />,
      title: intl.formatMessage(messages.connectErrorTitle),
      body: exitScreenTitle
    },
    [ExitReason.exited]: {
      icon: <Info48Regular />,
      title: intl.formatMessage(messages.exitedTitle),
      body: exitScreenTitle
    },
    [ExitReason.disconnected]: {
      icon: <PlugDisconnected48Regular />,
      title: intl.formatMessage(messages.disconnectedTitle),
      body: exitScreenTitle
    },
    [ExitReason.kicked]: {
      icon: <PersonWarning48Regular />,
      title: intl.formatMessage(messages.kickedTitle),
      body: exitScreenTitle
    },
    [ExitReason.idle]: {
      icon: <Timer48Regular />,
      title: intl.formatMessage(messages.idleTitle),
      body: exitScreenTitle
    },
    [ExitReason.backgrounded]: {
      icon: <Timer48Regular />,
      title: intl.formatMessage(messages.backgroundedTitle),
      body: exitScreenTitle
    },
    [ExitReason.concurrentSession]: {
      icon: <PhoneDesktop48Regular />,
      title: intl.formatMessage(messages.concurrentSessionTitle),
      body: exitScreenTitle
    },
    [ExitReason.deviceLinkedSession]: {
      icon: <PhoneDesktop48Regular />,
      title: intl.formatMessage(messages.deviceLinkedSessionTitle),
      body: exitScreenTitle
    },
    [ExitReason.full]: {
      icon: <PeopleErrorRegular />,
      title: intl.formatMessage(messages.fullTitle),
      body: exitScreenTitle
    },
    [ExitReason.versionMismatch]: {
      icon: <ArrowSyncCircle48Regular />,
      title: intl.formatMessage(messages.versionMismatchTitle),
      body: exitScreenTitle
    },
    [ExitReason.signInRequired]: {
      icon: <PersonPasskey48Regular />,
      title: intl.formatMessage(messages.signInRequiredTitle),
      body: exitScreenTitle
    },
    [ExitReason.rsvpRequired]: {
      icon: <CalendarLock48Regular />,
      title: intl.formatMessage(messages.rsvpRequiredTitle),
      body: exitScreenTitle
    },
    [ExitReason.notFound]: {
      icon: <QuestionCircle48Regular />,
      title: intl.formatMessage(messages.notFoundTitle),
      body: exitScreenTitle
    }
  };
  const exitReasonDetail = exitReasonDetails[reason];
  const shouldShowExitReasonDetail = Boolean(exitReasonDetail);
  const shouldShowRsvpContext = reason === ExitReason.rsvpRequired && eventData;
  const shouldShowRetryAction = reason === ExitReason.connectError || reason === ExitReason.sceneError;
  const shouldShowSignInAction = reason === ExitReason.signInRequired;
  const shouldShowRsvpAction = reason === ExitReason.rsvpRequired;
  const shouldShowExitReasonOnlyHomeAction =
    reason === ExitReason.denied || reason === ExitReason.notFound || reason === ExitReason.kicked;
  const canShowRejoinButton =
    !shouldShowSignInAction &&
    !shouldShowRsvpAction &&
    reason !== ExitReason.denied &&
    reason !== ExitReason.notFound &&
    reason !== ExitReason.kicked;
  const shouldShowExitReasonHomeAction =
    shouldShowExitReasonDetail &&
    shouldShowReturnHomeButton &&
    !canShowRejoinButton &&
    shouldShowExitReasonOnlyHomeAction;
  const shouldShowExitActions = shouldShowReturnHomeButton || canShowRejoinButton;

  const submitExitFeedback = useCallback(
    async (overall, label) => {
      if (feedbackStatus === "submitting" || (feedbackStatus === "submitted" && selectedFeedback === label)) {
        return;
      }

      setSelectedFeedback(label);
      setFeedbackStatus("submitting");

      try {
        const roomId = APP.hub?.hub_id || APP.hub?.id;

        await APP.dashboardChannel?.createRoomFeedback?.({
          roomId,
          overall,
          label,
          anonymousId: APP.store?.state?.profile?.id,
          exitReason: reason
        });
      } catch {
        // Feedback persistence is best effort; the selected button state is enough acknowledgement.
      }

      setFeedbackStatus("submitted");
    },
    [feedbackStatus, reason, selectedFeedback]
  );

  const completeSignInAndReload = useCallback(async token => {
    if (token && APP.store && APP.entryManager?.authChannel) {
      await completeMetaverseLoginAfterBridge({
        store: APP.store,
        authChannel: APP.entryManager.authChannel,
        hubChannel: APP.hubChannel,
        dashboardToken: token
      }).catch(() => undefined);
    }

    window.location.reload();
  }, []);

  const beginBridgeSignIn = useCallback(
    async onSuccess => {
      if (isPopupLoginPending) {
        return;
      }

      setPopupRetryReason(null);
      setIsPopupLoginPending(true);

      try {
        const token = await loginWithDashboardBridgePopup(window.location.href);
        await onSuccess(token);
      } catch (error) {
        const retryReason = getAuthPopupRetryReason(error, configs.AUTH0_POPUP_TIMEOUT_SECONDS);

        if (retryReason) {
          closeAuthPopupFromError(error);
          retryActionRef.current = onSuccess;
          setPopupRetryReason(retryReason);
        }
      } finally {
        setIsPopupLoginPending(false);
      }
    },
    [isPopupLoginPending]
  );

  const login = useCallback(async () => {
    await beginBridgeSignIn(completeSignInAndReload);
  }, [beginBridgeSignIn, completeSignInAndReload]);

  const onRetryPopupLogin = useCallback(() => {
    const retryAction = retryActionRef.current;
    retryActionRef.current = null;
    setPopupRetryReason(null);
    if (retryAction) {
      beginBridgeSignIn(retryAction);
    }
  }, [beginBridgeSignIn]);

  const onCancelPopupRetry = useCallback(() => {
    retryActionRef.current = null;
    setPopupRetryReason(null);
  }, []);

  let subtitle = null;
  if (reason === ExitReason.closed) {
    const contactEmail = intl.formatMessage({ id: "contact-email" });

    subtitle = (
      <>
        <div className={styles.exitReasonDetail}>
          <div className={styles.exitReasonIcon} aria-hidden="true">
            {exitReasonDetail.icon}
          </div>
          <h1 className={styles.exitReasonTitle}>{exitReasonDetail.title}</h1>
        </div>
        {showTerms && (
          <p
            className={classNames(
              styles.exitReasonSupportText,
              configManager.config.loadingScreen.useSceneBackground ? styles.textWhite : ""
            )}
          >
            <FormattedMessage
              id="exited-room-screen.closed-room-tos"
              defaultMessage="A Verse may be closed by the owner, or if we receive reports that it violates our <toslink>Terms of Use</toslink>."
              values={{
                // eslint-disable-next-line react/display-name
                toslink: chunks => (
                  <a target="_blank" rel="noreferrer noopener" href={termsUrl}>
                    {chunks}
                  </a>
                )
              }}
            />
          </p>
        )}
        <p
          className={classNames(
            styles.exitReasonSupportText,
            configManager.config.loadingScreen.useSceneBackground ? styles.textWhite : ""
          )}
        >
          <FormattedMessage
            id="exited-room-screen.contact-us"
            defaultMessage="If you have questions, contact us at {contactEmail}."
            values={{ contactEmail: <a href={`mailto:${contactEmail}`}>{contactEmail}</a> }}
          />
        </p>
        {shouldShowReturnHomeButton && (
          <div className={classNames(styles.exitActionButtons, styles.exitReasonActionButtons)}>
            <Button as="a" className={styles.exitActionButton} appearance="primary" href={leaveRoomUrl}>
              <FormattedMessage id="exited-room-screen.home-button" defaultMessage="Return to Home" />
            </Button>
          </div>
        )}
      </>
    );
  } else {
    const tcpUrl = new URL(document.location.toString());
    const tcpParams = new URLSearchParams(tcpUrl.search);
    tcpParams.set("force_tcp", true);
    tcpUrl.search = tcpParams.toString();

    subtitle = (
      <>
        {shouldShowRsvpContext && (
          <div className={styles.exitContext}>
            <h1 className={styles.exitContextTitle}>{eventData.title || eventData.name}</h1>
            {eventData.description && <p className={styles.exitContextDescription}>{eventData.description}</p>}
          </div>
        )}
        {shouldShowExitFeedback ? (
          <div className={styles.exitFeedbackHeader}>
            <h1 className={styles.exitFeedbackTitle}>{exitScreenTitle}</h1>
          </div>
        ) : shouldShowExitReasonDetail ? (
          <div className={styles.exitReasonDetail}>
            <div className={styles.exitReasonIcon} aria-hidden="true">
              {exitReasonDetail.icon}
            </div>
            <h1 className={styles.exitReasonTitle}>{exitReasonDetail.title}</h1>
            {exitReasonDetail.body && <p className={styles.exitReasonBody}>{exitReasonDetail.body}</p>}
          </div>
        ) : shouldShowRsvpContext ? (
          <p
            className={classNames(
              styles.exitReasonText,
              configManager.config.loadingScreen.useSceneBackground ? styles.textWhite : ""
            )}
          >
            {exitScreenTitle}
          </p>
        ) : (
          <h2
            className={classNames(
              styles.title,
              configManager.config.loadingScreen.useSceneBackground ? styles.textWhite : ""
            )}
          >
            {exitScreenTitle}
          </h2>
        )}
        {reason === ExitReason.connectError && (
          <p
            className={classNames(
              styles.exitReasonHelpText,
              configManager.config.loadingScreen.useSceneBackground ? styles.textWhite : ""
            )}
          >
            <FormattedMessage
              id="exited-room-screen.connect-tcp"
              defaultMessage="You can try <a>connecting via TCP</a>, which may work better on some networks."
              values={{
                // eslint-disable-next-line react/display-name
                a: chunks => <a href={tcpUrl.toString()}>{chunks}</a>
              }}
            />
          </p>
        )}

        {shouldShowExitFeedback && (
          <div className={styles.exitFeedbackContent}>
            <p className={styles.exitFeedbackQuestion}>
              <FormattedMessage
                id="exited-room-screen.feedback-question"
                defaultMessage="Did you enjoy the experience?"
              />
            </p>
            <div className={styles.exitFeedbackActions}>
              <Button
                className={classNames(
                  styles.exitFeedbackButton,
                  selectedFeedback === "liked" ? styles.exitFeedbackButtonSelected : ""
                )}
                appearance="secondary"
                aria-pressed={selectedFeedback === "liked"}
                onClick={() => submitExitFeedback(1, "liked")}
              >
                <span className={styles.exitFeedbackButtonContent}>
                  <ThumbLike24Regular />
                  <span>
                    <FormattedMessage id="exited-room-screen.feedback-liked" defaultMessage="Like" />
                  </span>
                </span>
              </Button>
              <Button
                className={classNames(
                  styles.exitFeedbackButton,
                  selectedFeedback === "disliked" ? styles.exitFeedbackButtonSelected : ""
                )}
                appearance="secondary"
                aria-pressed={selectedFeedback === "disliked"}
                onClick={() => submitExitFeedback(-1, "disliked")}
              >
                <span className={styles.exitFeedbackButtonContent}>
                  <ThumbDislike24Regular />
                  <span>
                    <FormattedMessage id="exited-room-screen.feedback-disliked" defaultMessage="Dislike" />
                  </span>
                </span>
              </Button>
            </div>
            {feedbackStatus === "submitted" && (
              <p className={styles.exitFeedbackStatus}>
                <FormattedMessage
                  id="exited-room-screen.feedback-submitted"
                  defaultMessage="Thanks for your feedback."
                />
              </p>
            )}
            {shouldShowExitActions && (
              <div className={styles.exitActionButtons}>
                {shouldShowReturnHomeButton && (
                  <Button as="a" className={styles.exitActionButton} appearance="primary" href={leaveRoomUrl}>
                    <FormattedMessage id="exited-room-screen.home-button" defaultMessage="Return to Home" />
                  </Button>
                )}

                {canShowRejoinButton && (
                  <Button
                    as="a"
                    className={styles.exitActionButton}
                    appearance={shouldShowReturnHomeButton ? "secondary" : "primary"}
                    href={window.location.href}
                  >
                    <FormattedMessage id="exited-room-screen.rejoin-button" defaultMessage="Rejoin" />
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {shouldShowSignInAction && (
          <div className={classNames(styles.exitActionButtons, styles.exitReasonActionButtons)}>
            <Button
              className={styles.exitActionButton}
              appearance="primary"
              onClick={login}
              disabled={isPopupLoginPending}
            >
              <FormattedMessage id="exited-room-screen.sign-in-button" defaultMessage="Sign In" />
            </Button>
            {shouldShowReturnHomeButton && (
              <Button as="a" className={styles.exitActionButton} appearance="secondary" href={leaveRoomUrl}>
                <FormattedMessage id="exited-room-screen.home-button" defaultMessage="Return to Home" />
              </Button>
            )}
          </div>
        )}
        {shouldShowRsvpAction && (
          <>
            <div className={classNames(styles.exitActionButtons, styles.exitReasonActionButtons)}>
              <Button as="a" className={styles.exitActionButton} appearance="primary" href={rsvpUrl}>
                <FormattedMessage id="exited-room-screen.rsvp-button" defaultMessage="RSVP" />
              </Button>
              {shouldShowReturnHomeButton && (
                <Button as="a" className={styles.exitActionButton} appearance="secondary" href={leaveRoomUrl}>
                  <FormattedMessage id="exited-room-screen.home-button" defaultMessage="Return to Home" />
                </Button>
              )}
            </div>
            <p className={styles.exitRsvpHint}>
              <FormattedMessage
                id="exited-room-screen.rsvp-hint"
                defaultMessage="Already RSVP’d? Just sign in to join."
              />
              &nbsp;
              <a
                href="#"
                onClick={event => {
                  event.preventDefault();
                  login();
                }}
                aria-disabled={isPopupLoginPending}
              >
                <FormattedMessage id="exited-room-screen.sign-in-button" defaultMessage="Sign In" />
              </a>
            </p>
          </>
        )}

        {shouldShowExitReasonHomeAction && (
          <div className={classNames(styles.exitActionButtons, styles.exitReasonActionButtons)}>
            <Button as="a" className={styles.exitActionButton} appearance="primary" href={leaveRoomUrl}>
              <FormattedMessage id="exited-room-screen.home-button" defaultMessage="Return to Home" />
            </Button>
          </div>
        )}

        {!shouldShowExitFeedback && canShowRejoinButton && (
          <>
            {shouldShowExitReasonDetail ? (
              <div className={classNames(styles.exitActionButtons, styles.exitReasonActionButtons)}>
                <Button as="a" className={styles.exitActionButton} appearance="primary" href={window.location.href}>
                  {shouldShowRetryAction ? (
                    <FormattedMessage id="exited-room-screen.retry-button" defaultMessage="Retry" />
                  ) : (
                    <FormattedMessage id="exited-room-screen.rejoin-button" defaultMessage="Rejoin" />
                  )}
                </Button>
                {shouldShowReturnHomeButton && (
                  <Button as="a" className={styles.exitActionButton} appearance="secondary" href={leaveRoomUrl}>
                    <FormattedMessage id="exited-room-screen.home-button" defaultMessage="Return to Home" />
                  </Button>
                )}
              </div>
            ) : (
              <Button as="a" appearance="primary" href={window.location.href}>
                <FormattedMessage id="exited-room-screen.rejoin-button" defaultMessage="Rejoin" />
              </Button>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <>
      <LoadingScreenLayout center={subtitle} showLogo={false} />
      {popupRetryReason && (
        <AuthPopupRetryDialog
          reason={popupRetryReason}
          onRetry={onRetryPopupLogin}
          onCancel={onCancelPopupRetry}
          onClose={onCancelPopupRetry}
        />
      )}
    </>
  );
}

ExitedRoomScreen.propTypes = {
  reason: PropTypes.string.isRequired,
  showTerms: PropTypes.bool,
  termsUrl: PropTypes.string,
  leaveRoomUrl: PropTypes.string
};
